import { ActionContext, ActionTree } from 'vuex';
import LagerService from '@/modules/ewerp/service/LagerService';

import { RootState } from '@/modules/ewerp/store';
import { useEwerpCommonStore } from '@/modules/ewerp/store/ewerpCommonStore';

import { State } from './state';

import { Mutations, MutationTypes } from './mutations';

import { Flasche, Kapsel, Karton } from '@/modules/ewerp/models/PpsMaterialModels';
import { Materialartikel } from '@/modules/ewerp/models/LagerMaterialModels';

export enum ActionTypes {
  GetFlaschenBestand = 'GETFLASCHENBESTAND',
  UpdateGetFlaschenBestand = 'UPDATEGETFLASCHENBESTAND',
  GetKartonsBestand = 'GETKARTONSBESTAND',
  UpdateGetKartonsBestand = 'UPDATEGETKARTONSBESTAND',
  GetKapselnBestand = 'GETKAPSELNBESTAND',
  UpdateGetKapselnBestand = 'UPDATEGETKAPSELNBESTAND',
  GetMaterialartikelFlaschen = 'GETMATERIALARTIKELFLASCHEN',

  CreateWMSMaterialBestand = 'CREATEWMSMATERIALBESTAND',
  WMSUpdateMaterialBestand = 'WMSUPDATEMATERIALBESTAND',
  WMSMaterialBestandUmlagern = 'WMSMATERIALBESTANDUMLAGERN',
  WMSMaterialBestandAuslagern = 'WMSMATERIALBESTANDAUSLAGERN',
  WMSMaterialBestandVerbraucht = 'WMSMATERIALBESTANDVERBRAUCHT',
  WMSMaterialBestandImport = 'WMSMATERIALBESTANDIMPORT',
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  [ActionTypes.GetFlaschenBestand](context: ActionArguments): Promise<void>;
  [ActionTypes.UpdateGetFlaschenBestand](context: ActionArguments): Promise<void>;
  [ActionTypes.GetKartonsBestand](context: ActionArguments): Promise<void>;
  [ActionTypes.UpdateGetKartonsBestand](context: ActionArguments): Promise<void>;
  [ActionTypes.GetKapselnBestand](context: ActionArguments): Promise<void>;
  [ActionTypes.UpdateGetKapselnBestand](context: ActionArguments): Promise<void>;
  [ActionTypes.GetMaterialartikelFlaschen](context: ActionArguments): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  async [ActionTypes.GetFlaschenBestand]({ commit }) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.flaschenBestandLoading = true;
    status.flaschenBestandErrorMsg = '';
    commonStore.setStatus(status);

    return LagerService.getAlleFlaschenBestand()
      .then((res) => {
        status.flaschenBestandLoading = false;
        commonStore.setStatus(status);

        const result: Array<Flasche> = res.data;
        commit(MutationTypes.GetFlaschenbestandSuccess, result);
      })
      .catch((error) => {
        status.flaschenBestandLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.flaschenBestandErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.flaschenBestandErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.flaschenBestandErrorMsg = error;
        }

        sessionStorage.removeItem('materialFlaschen');
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.UpdateGetFlaschenBestand]({ commit }) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.flaschenBestandErrorMsg = '';
    commonStore.setStatus(status);

    return LagerService.getAlleFlaschenBestand()
      .then((res) => {
        commonStore.setStatus(status);

        const result: Array<Flasche> = res.data;
        commit(MutationTypes.GetFlaschenbestandSuccess, result);
      })
      .catch((error) => {
        // console.log(error);
        if (error.message === 'Network Error') {
          status.flaschenBestandErrorMsg = 'Es konnt keine Verbindung zum Server hergestellt werden.';
        } else if (error.response?.status == 401) {
          status.flaschenBestandErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.flaschenBestandErrorMsg = error;
        }

        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.GetKartonsBestand]({ commit }) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.kartonsBestandErrorMsg = '';
    status.kartonsBestandLoading = true;
    commonStore.setStatus(status);

    return LagerService.getAlleKartonsBestand()
      .then((res) => {
        status.kartonsBestandLoading = false;
        commonStore.setStatus(status);

        const result: Array<Karton> = res.data;
        commit(MutationTypes.GetKartonsbestandSuccess, result);

        // dispatch(ActionTypes.GetMaterialartikelFlaschen)
        //   .then(() => {
        //     commit(MutationTypes.SetAnzahlFlaschen, undefined)
        //   })
      })
      .catch((error) => {
        status.kartonsBestandLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.kartonsBestandErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.kartonsBestandErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.kartonsBestandErrorMsg = error;
        }

        sessionStorage.removeItem('materialKartons');
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.UpdateGetKartonsBestand]({ commit }) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.kartonsBestandErrorMsg = '';
    commonStore.setStatus(status);

    return LagerService.getAlleKartonsBestand()
      .then((res) => {
        commonStore.setStatus(status);

        const result: Array<Karton> = res.data;
        commit(MutationTypes.GetKartonsbestandSuccess, result);
      })
      .catch((error) => {
        // console.log(error);
        if (error.message === 'Network Error') {
          status.kartonsBestandErrorMsg = error.message;
        } else if (error.response?.status == 403) {
          status.kartonsBestandErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.kartonsBestandErrorMsg = error;
        }

        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.GetKapselnBestand]({ commit }) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.kapselnBestandErrorMsg = '';
    status.kapselnBestandLoading = true;
    commonStore.setStatus(status);

    return LagerService.getAlleKapselnBestand()
      .then((res) => {
        status.kapselnBestandErrorMsg = '';
        const result: Array<Kapsel> = res.data;
        commit(MutationTypes.GetKapselnbestandSuccess, result);
      })
      .catch((error) => {
        // console.log(error);
        if (error.message === 'Network Error') {
          status.kapselnBestandErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.kapselnBestandErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.kapselnBestandErrorMsg = error;
        }

        sessionStorage.removeItem('materialKapseln');
      })
      .finally(() => {
        status.kapselnBestandLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.UpdateGetKapselnBestand]({ commit }) {
    return LagerService.getAlleKapselnBestand()
      .then((res) => {
        const result: Array<Kapsel> = res.data;
        commit(MutationTypes.GetKapselnbestandSuccess, result);
      })
      .catch((error) => {
        const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
        status.kapselnBestandLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.kapselnBestandErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.kapselnBestandErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.kapselnBestandErrorMsg = error;
        }

        sessionStorage.removeItem('materialKapseln');
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.GetMaterialartikelFlaschen]({ commit }) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.materialartikelFlaschenLoading = true;
    commonStore.setStatus(status);

    return LagerService.getAlleMaterialartikelFlaschen()
      .then((res) => {
        status.materialartikelFlaschenLoading = false;
        commonStore.setStatus(status);

        const result: Array<Materialartikel> = res.data;
        commit(MutationTypes.GetMaterialartikelFlaschenSuccss, result);
      })
      .catch((error) => {
        status.materialartikelFlaschenLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.materialartikelFlaschenErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.materialartikelFlaschenErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.materialartikelFlaschenErrorMsg = error;
        }

        commonStore.setStatus(status);
      });
  },
};
